/**
 * DataStructurePanel — V14.62 Sprint 18 (Lab).
 *
 * Panneau droit masquable qui affiche en temps réel le micro-IFC produit
 * par le HybridEditor. C'est la preuve de concept que :
 *   1. La structure de données est ultra-légère (~200 octets par mur)
 *   2. Toute modification canvas se reflète instantanément dans le JSON
 *   3. Ce JSON est self-contained → peut être envoyé tel quel à un endpoint
 *      `/api/geometry/render-3d` sans aucune image attachée.
 */
import { useMemo, useState } from 'react';
import {
  PanelRightClose, PanelRightOpen, Copy, Check, Download,
} from 'lucide-react';
import { toast } from 'sonner';
import { useGeometryStore } from './useGeometryStore';
import ExportButton from './ExportButton';

const DataStructurePanel = () => {
  const [collapsed, setCollapsed] = useState(false);
  const [copied, setCopied] = useState(false);
  const walls = useGeometryStore((s) => s.walls);
  const windows = useGeometryStore((s) => s.windows);
  const doors = useGeometryStore((s) => s.doors);
  const stairs = useGeometryStore((s) => s.stairs);
  const terrain = useGeometryStore((s) => s.terrain);
  const skew_edges = useGeometryStore((s) => s.skew_edges);
  const velux = useGeometryStore((s) => s.velux);
  const scale = useGeometryStore((s) => s.scale_m_per_px);

  const payload = useMemo(() => ({
    scale_m_per_px: +scale.toFixed(6),
    walls: walls.map(({ id, x1, y1, x2, y2, thickness, kind }) => ({
      id, x1, y1, x2, y2, thickness,
      kind: kind || 'cloison_fine',
    })),
    windows: windows.map(({ id, wallId, t, width }) => ({
      id, wallId, t: +t.toFixed(3), width: +width.toFixed(3),
    })),
    doors: doors.map(({ id, wallId, t, width }) => ({
      id, wallId, t: +t.toFixed(3), width: +width.toFixed(3),
    })),
    stairs: stairs.map(({ id, x, y, w, h, kind }) => ({
      id, x, y, w, h, kind,
    })),
    terrain: terrain.map(({ x, y }) => ({ x: Math.round(x), y: Math.round(y) })),
    // Sprint 31 Phase 1 — Lignes de brisure (mansardes)
    skew_edges: (skew_edges || []).map(({ id, x1, y1, x2, y2, low_side_hsp_m }) => ({
      id, x1, y1, x2, y2,
      ...(Number.isFinite(low_side_hsp_m) && low_side_hsp_m > 0
        ? { low_side_hsp_m: +(+low_side_hsp_m).toFixed(2) }
        : {}),
    })),
    // Sprint 31 Phase 2 — Velux (fenêtres de toit)
    velux: (velux || []).map(({ id, skewId, t, width_m, height_m }) => ({
      id, skewId,
      t: +(+t).toFixed(3),
      width_m: +(+width_m).toFixed(2),
      height_m: +(+height_m).toFixed(2),
    })),
  }), [scale, walls, windows, doors, stairs, terrain, skew_edges, velux]);

  const payloadString = useMemo(() => JSON.stringify(payload, null, 2), [payload]);
  const byteSize = useMemo(() => new Blob([payloadString]).size, [payloadString]);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(payloadString);
      setCopied(true);
      toast.success('JSON copié dans le presse-papier');
      setTimeout(() => setCopied(false), 1200);
    } catch {
      toast.error('Copie impossible');
    }
  };

  const handleDownload = () => {
    const blob = new Blob([payloadString], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `micro-ifc-${Date.now()}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  if (collapsed) {
    return (
      <div className="w-9 shrink-0 flex flex-col items-center py-2 gap-2 rounded-lg border"
           style={{ background: 'rgba(8,12,20,0.6)', borderColor: 'rgba(16,185,129,0.2)' }}
           data-testid="data-structure-panel-collapsed">
        <button type="button"
                onClick={() => setCollapsed(false)}
                className="w-7 h-7 inline-flex items-center justify-center rounded hover:bg-white/5 text-gray-400 hover:text-emerald-300"
                title="Déplier le panneau Data Structure"
                data-testid="data-structure-expand-btn">
          <PanelRightOpen className="w-4 h-4" />
        </button>
        <div className="flex-1 flex items-center justify-center">
          <span className="text-[9.5px] text-gray-500 select-none whitespace-nowrap"
                style={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)' }}>
            JSON · {walls.length} murs · {windows.length} fenêtres
          </span>
        </div>
      </div>
    );
  }

  return (
    <aside className="w-[320px] shrink-0 flex flex-col rounded-lg border overflow-hidden"
           style={{ background: 'rgba(8,12,20,0.8)', borderColor: 'rgba(16,185,129,0.25)' }}
           data-testid="data-structure-panel">
      {/* Header */}
      <div className="flex items-center gap-2 px-3 py-2 border-b"
           style={{ borderColor: 'rgba(16,185,129,0.2)' }}>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-[0.2em] text-emerald-300/80 leading-none">
            Micro-IFC · Live
          </div>
          <div className="text-[10.5px] text-gray-400 mt-0.5">
            <span className="font-mono">{walls.length}</span> murs ·{' '}
            <span className="font-mono">{windows.length}</span> fen. ·{' '}
            <span className="font-mono">{doors.length}</span> portes ·{' '}
            <span className="font-mono">{stairs.length}</span> esc. ·{' '}
            <span className="font-mono">{skew_edges.length}</span> mansardes ·{' '}
            <span className="font-mono">{velux.length}</span> velux ·{' '}
            <span className="font-mono">{terrain.length}</span> pts terrain ·{' '}
            <span className="font-mono">{byteSize}</span> o
          </div>
        </div>
        <button type="button" onClick={handleCopy}
                className="w-7 h-7 inline-flex items-center justify-center rounded hover:bg-emerald-500/15 text-gray-300 hover:text-emerald-300"
                title="Copier le JSON"
                data-testid="data-structure-copy-btn">
          {copied ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <Copy className="w-3.5 h-3.5" />}
        </button>
        <button type="button" onClick={handleDownload}
                className="w-7 h-7 inline-flex items-center justify-center rounded hover:bg-emerald-500/15 text-gray-300 hover:text-emerald-300"
                title="Télécharger en .json"
                data-testid="data-structure-download-btn">
          <Download className="w-3.5 h-3.5" />
        </button>
        <button type="button"
                onClick={() => setCollapsed(true)}
                className="w-7 h-7 inline-flex items-center justify-center rounded hover:bg-white/5 text-gray-400 hover:text-gray-200"
                title="Replier le panneau"
                data-testid="data-structure-collapse-btn">
          <PanelRightClose className="w-4 h-4" />
        </button>
      </div>

      {/* JSON viewer */}
      <pre className="flex-1 overflow-auto m-0 px-3 py-2 text-[10.5px] leading-snug font-mono text-emerald-100/90 selection:bg-emerald-500/30"
           data-testid="data-structure-json"
           style={{ background: 'rgba(2,6,12,0.6)' }}>
        {payloadString}
      </pre>

      {/* Footer hint */}
      <div className="px-3 py-2 text-[9.5px] text-gray-500 border-t leading-snug"
           style={{ borderColor: 'rgba(16,185,129,0.2)' }}>
        Ce JSON est le payload exact envoyé au moteur 3D — aucune image
        n&apos;accompagne le rendu. Schema-compatible IFC4.
      </div>

      {/* PRIMARY EXPORT BUTTON — gated on calibration */}
      <div className="px-3 py-2.5 border-t bg-black/30"
           style={{ borderColor: 'rgba(16,185,129,0.2)' }}>
        <ExportButton />
      </div>
    </aside>
  );
};

export default DataStructurePanel;
